import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Box, Typography, Grid, Paper, Tooltip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { DateTime } from 'luxon';
import { useSearchParams } from 'react-router-dom';
import { useSettingsStore, useApiStore } from '@core/store';
import { NewPageTitleBar, LoadingSpinner } from '@/components';
import NewWcnTable, { DataTableColumn } from '@/components/datatable/NewWcnTable';
import AllShopsSelector from '@/components/selectors/AllShopsSelector';
import AllHealthsSelector from '@/components/selectors/AllHealthsSelector';
import { STATUS_COLORS } from '@core/types';

import { apiClient } from '@core/api';
import { getMenuData } from '@core/data/menuData';
import { FCSecurePage, useRegisterRefresh, useShopPersistence } from '@/components/shared/navigation/securePage.ts';

interface HealthStatus {
    hybridLoc: string;
    scanner: string;
    camera: string;
}

interface FastLaner {
    trolleyId: string;
    trolleySerial: string;
    trolleyMac: string;
    batteryState: string;
    lastActivity: string;
    osVersion: string;
    healthStatus: HealthStatus;
    groupId: string;
    isCharging: boolean;
    apkVersions: string[];
}

const HEALTH_STATES = {
    good: {
        id: 'Good',
        value: 'goodHealth',
        icon: 'zmdi-circle',
        color: 'success',
        order: 1
    },
    bad: {
        id: 'Bad',
        value: 'badHealth',
        icon: 'zmdi-circle',
        color: 'error',
        order: 0
    }
} as const;

const reloadInterval = 5 * 60 * 1000; // 5 minutes

const FastLanerHealthMonitoring: FCSecurePage = () => {
    const { t } = useTranslation();
    const { selectedLocale } = useSettingsStore();
    const { shopIds } = useApiStore();
    const [searchParams] = useSearchParams();

    const [loading, setLoading] = useState(false);
    const { selectedShopIds, setSelectedShopIds } = useShopPersistence();
    const [selectedHealths, setSelectedHealths] = useState<string[]>([HEALTH_STATES.good.id, HEALTH_STATES.bad.id]);
    const [allFastLaners, setAllFastLaners] = useState<FastLaner[]>([]);
    const [lastUpdate, setLastUpdate] = useState<DateTime | undefined>(undefined);

    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        const shopId = searchParams.get('shopId');
        if (shopId) {
            setSelectedShopIds([shopId]);
        }
    }, [searchParams]);

    useEffect(() => {
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, []);

    const loadFastLaners = useCallback(async () => {
        if (selectedShopIds.length === 0) {
            return;
        }

        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }

        setLoading(true);

        try {
            const selectedShopsData = shopIds.filter((shop) => selectedShopIds.includes(shop.id));
            const groupIds = [
                ...new Set(
                    selectedShopsData
                        .flatMap((shop) => (shop as any).fastLanerGroupIds || [])
                        .filter((groupId) => groupId != null)
                )
            ];

            if (groupIds.length === 0) {
                console.warn('No FastLaner group IDs found for selected shops');
                setAllFastLaners([]);
                setLastUpdate(DateTime.now());
                setLoading(false);
                return;
            }

            const queryParams = groupIds.map((groupId) => `groupId=${encodeURIComponent(groupId)}`).join('&');
            const response = await apiClient.get<any>(`fastlaner/trolley-data?${queryParams}`);

            const allFastLaners = response.data?.data;
            const trolleys = allFastLaners ? allFastLaners.flatMap((group: any) => group.trolleys || []) : [];

            setAllFastLaners(trolleys);
        } catch (error) {
            console.error('Error fetching FastLaner health data:', error);
            setAllFastLaners([]);
        } finally {
            setLastUpdate(DateTime.now());
            setLoading(false);
            timeoutRef.current = setTimeout(() => {
                loadFastLaners();
            }, reloadInterval);
        }
    }, [selectedShopIds, shopIds]);

    useEffect(() => {
        setAllFastLaners([]);
        if (selectedShopIds.length > 0) {
            loadFastLaners();
        }
    }, [selectedShopIds]);

    useEffect(() => {
        if (allFastLaners.length > 0) {
            setLastUpdate(DateTime.now());
        }
    }, [allFastLaners, selectedLocale.locale]);

    useRegisterRefresh(loadFastLaners, selectedShopIds.length === 0);

    const equalsIgnoreCase = (str1?: string, str2?: string): boolean => {
        if (!str1 || !str2) return false;
        return str1.localeCompare(str2, undefined, { sensitivity: 'base' }) === 0;
    };

    const isTimeWithin10Minutes = (utcTimeString: string): boolean => {
        const givenTime = DateTime.fromISO(utcTimeString, { zone: 'utc' });
        const currentTime = DateTime.utc();
        const diffInMinutes = currentTime.diff(givenTime, 'minutes').minutes;
        return diffInMinutes <= 10;
    };

    const getConnectedDeviceHealthState = (deviceStatus: string) => {
        return equalsIgnoreCase(deviceStatus, 'connected') ? HEALTH_STATES.good : HEALTH_STATES.bad;
    };

    const getConnectionHealthState = (fastLaner: FastLaner) => {
        return isTimeWithin10Minutes(fastLaner.lastActivity) ? HEALTH_STATES.good : HEALTH_STATES.bad;
    };

    const getHealthState = useCallback((fastLaner: FastLaner) => {
        const deviceStates = Object.values(fastLaner.healthStatus);
        const allDevicesHealthy = deviceStates.every(
            (status) => getConnectedDeviceHealthState(status) === HEALTH_STATES.good
        );
        const connectionHealthy = getConnectionHealthState(fastLaner) === HEALTH_STATES.good;

        return allDevicesHealthy && connectionHealthy ? HEALTH_STATES.good : HEALTH_STATES.bad;
    }, []);

    const getBatteryColor = (batteryState: string): string => {
        const battery = Number.parseInt(batteryState, 10);
        if (Number.isNaN(battery)) return '#9E9E9E';
        if (battery < 20) return STATUS_COLORS.error;
        if (battery < 30) return '#FFEB3B';
        return STATUS_COLORS.success;
    };

    const filteredFastLaners = React.useMemo(() => {
        if (!allFastLaners.length || !selectedHealths.length) return [];

        return allFastLaners.filter((fastLaner) => {
            const healthState = getHealthState(fastLaner);
            return selectedHealths.includes(healthState.id);
        });
    }, [allFastLaners, selectedHealths, getHealthState]);

    const formatDateTime = (dateString: string) => {
        return DateTime.fromISO(dateString, { zone: 'utc' })
            .toLocal()
            .setLocale(selectedLocale.locale)
            .toLocaleString(DateTime.DATETIME_SHORT);
    };

    const renderHealthIcon = (healthState: typeof HEALTH_STATES.good | typeof HEALTH_STATES.bad) => {
        const colorValue = STATUS_COLORS[healthState.color as keyof typeof STATUS_COLORS];
        return (
            <Box
                component="i"
                className={`zmdi ${healthState.icon}`}
                sx={{
                    fontSize: '24px',
                    color: `${colorValue} !important`,
                    mr: 1
                }}
            />
        );
    };

    const renderBatteryState = (batteryState: string) => {
        const icon = batteryState ? 'zmdi-battery-flash' : 'zmdi-battery-unknown';
        const color = getBatteryColor(batteryState);

        return (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Box
                    component="i"
                    className={`zmdi ${icon}`}
                    sx={{
                        fontSize: '20px',
                        color: `${color} !important`,
                        mr: 1
                    }}
                />
                <Typography variant="body2">{batteryState}</Typography>
            </Box>
        );
    };

    const customSort = useCallback((data: any[], sortBy: string, sortDir: 'asc' | 'desc') => {
        return [...data].sort((a, b) => {
            if (sortBy === 'batteryState') {
                const aNum = parseInt(a.batteryState?.replace('%', '') || '0', 10);
                const bNum = parseInt(b.batteryState?.replace('%', '') || '0', 10);
                return sortDir === 'desc' ? bNum - aNum : aNum - bNum;
            }

            if (sortBy === 'osVersion') {
                const aVersion = a.osVersion || '0.0.0';
                const bVersion = b.osVersion || '0.0.0';

                const aParts = aVersion.split('.').map((n: string) => parseInt(n, 10) || 0);
                const bParts = bVersion.split('.').map((n: string) => parseInt(n, 10) || 0);

                const maxLength = Math.max(aParts.length, bParts.length);
                for (let i = 0; i < maxLength; i++) {
                    const aNum = aParts[i] || 0;
                    const bNum = bParts[i] || 0;
                    if (aNum !== bNum) {
                        return sortDir === 'desc' ? bNum - aNum : aNum - bNum;
                    }
                }
                return 0;
            }

            // Default sorting for other columns
            const aVal = (a as any)[sortBy];
            const bVal = (b as any)[sortBy];

            if (aVal == null) return 1;
            if (bVal == null) return -1;

            if (typeof aVal === 'string' && typeof bVal === 'string') {
                return sortDir === 'desc' ? bVal.localeCompare(aVal) : aVal.localeCompare(bVal);
            }

            return sortDir === 'desc' ? Number(bVal) - Number(aVal) : Number(aVal) - Number(bVal);
        });
    }, []);

    const tableColumns: DataTableColumn<FastLaner>[] = [
        {
            value: 'healthState',
            text: '',
            sortable: true,
            align: 'center',
            width: 100,
            render: (fastLaner) => (
                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                    {renderHealthIcon(getHealthState(fastLaner))}
                </Box>
            )
        },
        {
            value: 'trolleySerial',
            text: t('trolleyId'),
            sortable: true,
            width: 160,
            render: (fastLaner) => <Typography variant="body2">{fastLaner.trolleySerial}</Typography>
        },
        {
            value: 'batteryState',
            text: t('battery'),
            sortable: true,
            width: 160,
            render: (fastLaner) => renderBatteryState(fastLaner.batteryState)
        },
        {
            value: 'connection',
            text: t('connection'),
            sortable: true,
            width: 160,
            render: (fastLaner) => {
                const connectionState = getConnectionHealthState(fastLaner);
                const isOnline = connectionState.id === 'Good';
                return (
                    <Tooltip
                        title={
                            <Box>
                                <Typography variant="body2">{isOnline ? t('online') : t('offline')}</Typography>
                                <Typography variant="caption">
                                    {t('lastActivity')}: {formatDateTime(fastLaner.lastActivity)}
                                </Typography>
                            </Box>
                        }
                        placement="right"
                    >
                        <span>{renderHealthIcon(connectionState)}</span>
                    </Tooltip>
                );
            }
        },
        {
            value: 'healthStatus.hybridLoc',
            text: t('hybridLoc'),
            sortable: true,
            width: 160,
            render: (fastLaner) => renderHealthIcon(getConnectedDeviceHealthState(fastLaner.healthStatus.hybridLoc))
        },
        {
            value: 'healthStatus.scanner',
            text: t('scanner'),
            sortable: true,
            width: 160,
            render: (fastLaner) => renderHealthIcon(getConnectedDeviceHealthState(fastLaner.healthStatus.scanner))
        },
        {
            value: 'healthStatus.camera',
            text: t('camera'),
            sortable: true,
            width: 160,
            render: (fastLaner) => renderHealthIcon(getConnectedDeviceHealthState(fastLaner.healthStatus.camera))
        },
        {
            value: 'osVersion',
            text: t('firmware'),
            sortable: true,
            width: 160,
            render: (fastLaner) => <Typography variant="body2">{fastLaner.osVersion}</Typography>
        },
        {
            value: 'lastActivity',
            text: t('lastActivity'),
            sortable: true,
            width: 200,
            render: (fastLaner) => <Typography variant="body2">{formatDateTime(fastLaner.lastActivity)}</Typography>
        }
    ];

    return (
        <Box>
            <NewPageTitleBar
                icon={<i className="bi bi-cart3" style={{ fontSize: '16px' }} />}
                title={t('fastLanerHealth')}
                menus={getMenuData()}
                iconSize="18px"
                currentRoute={{ path: '/statistics/fastLaner/health' }}
                t={t}
                hasAutoRefresh={true}
                lastUpdate={lastUpdate}
            />

            <Box sx={{ px: 3, pb: 3, mt: 1 }}>
                <Grid container spacing={3} sx={{ mb: 6 }}>
                    <Grid
                        item
                        xs={6}
                        md={3}
                        sx={{ flexBasis: { md: '560px' }, maxWidth: { md: '560px' }, flexGrow: { md: 0 } }}
                    >
                        <AllShopsSelector onChange={setSelectedShopIds} value={selectedShopIds} label={t('shop')} />
                    </Grid>
                    <Grid item xs={6} md={3}>
                        <AllHealthsSelector
                            healthStates={HEALTH_STATES}
                            selectedOnStart={[HEALTH_STATES.good.id, HEALTH_STATES.bad.id]}
                            onSelectedHealthsChanged={setSelectedHealths}
                        />
                    </Grid>
                </Grid>

                {!filteredFastLaners.length && !loading && selectedShopIds.length > 0 && (
                    <Box sx={{ textAlign: 'center', py: 8 }}>
                        <Typography variant="h6" color="text.secondary">
                            {t('noData')}
                        </Typography>
                    </Box>
                )}

                {selectedShopIds.length === 0 && (
                    <Box sx={{ textAlign: 'center', py: 8 }}>
                        <Typography variant="h6" color="text.secondary">
                            {t('noData')}
                        </Typography>
                    </Box>
                )}

                {loading && selectedShopIds.length > 0 && (
                    <LoadingSpinner showBackground={false} showMessage={true} message={t('loadingData')} size={60} />
                )}

                {!loading && filteredFastLaners.length > 0 && (
                    <div className="data-table__wrapper">
                        <Paper component="div">
                            <NewWcnTable
                                columns={tableColumns}
                                data={filteredFastLaners}
                                sorting={{ defaultSortBy: 'healthState' }}
                                customSort={customSort}
                                size="small"
                                noDataMessage={t('noData')}
                            />
                        </Paper>
                    </div>
                )}
            </Box>
        </Box>
    );
};

export default FastLanerHealthMonitoring;
